import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Box,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Checkbox,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  arrayMove,
  sortableKeyboardCoordinates,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import BasicButton from '../../../components/button/BasicButton';
import AdminBulkDialog from './AdminBulkDialog';
import useAdminBulkAction from '../useAdminBulkAction';
import useAdminDraftGuard from '../useAdminDraftGuard';
import { useUserStore } from '../../../stores/userStore';
import ProgressBar from '../../../components/common/ProgressBar';
import NoData from '../../../components/common/NoData';
import { adminApi } from '../../../api/admin';
import theme from '../../../theme';

const HEAD_CELLS = [
  { id: 'artistName', label: '아티스트' },
  { id: 'albumTitle', label: '앨범' },
  { id: 'genre', label: '장르' },
  { id: 'releaseDate', label: '발매일' },
  { id: 'rating', label: '평점' },
  { id: 'reviewCount', label: '리뷰수' },
  { id: 'isAdMarked', label: '광고 마크' },
  { id: 'displayRegion', label: '분류' },
];

const getAlbumUrl = (album) =>
  album.slug
    ? `/reviews/${album.slug}`
    : `/reviews/${album.albumSource?.toLowerCase()}/${album.sourceId}`;

const SectionTitle = ({ title }) => {
  const [leftTitle, rightTitle] = title.split('|').map((part) => part.trim());
  const hasDivider = Boolean(rightTitle);

  if (!hasDivider) {
    return (
      <Typography
        fontSize={{ xs: 18, md: 20, lg: 24 }}
        fontWeight="bold"
        color={theme.palette.black[9]}
        sx={{ mb: { xs: '20px', md: '30px' } }}
      >
        {title}
      </Typography>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={{ xs: 1, md: 1.25 }}
      sx={{ mb: { xs: '20px', md: '30px' } }}
    >
      <Typography
        component="span"
        fontSize={{ xs: 18, md: 20, lg: 24 }}
        fontWeight="bold"
        color={theme.palette.black[9]}
        lineHeight={1.2}
      >
        {leftTitle}
      </Typography>
      <Box
        sx={{
          width: '1px',
          height: { xs: 12, md: 14, lg: 16 },
          backgroundColor: theme.palette.black[3],
        }}
      />
      <Typography
        component="span"
        fontSize={{ xs: 18, md: 20, lg: 24 }}
        fontWeight="bold"
        color={theme.palette.black[9]}
        lineHeight={1.2}
      >
        {rightTitle}
      </Typography>
    </Stack>
  );
};

const SortableAlbumRow = ({ album, cellStyle, textStyle, disabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: album.id,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow ref={setNodeRef} style={style} hover>
      <TableCell sx={{ ...cellStyle, p: 0 }}>
        <IconButton
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          disabled={disabled}
          aria-label={`${album.title} 순서 이동`}
          sx={{ cursor: 'grab', touchAction: 'none' }}
        >
          <DragIndicatorIcon sx={{ color: theme.palette.black[3] }} />
        </IconButton>
      </TableCell>
      <AlbumCells album={album} cellStyle={cellStyle} textStyle={textStyle} />
    </TableRow>
  );
};

const AlbumCells = ({ album, cellStyle, textStyle }) => {
  const artist = album.artist || {};
  const artistPath = artist.slug
    ? `/artists/${artist.slug}`
    : artist.sourceId && artist.artistSource
      ? `/artists/${artist.artistSource.toLowerCase()}/${artist.sourceId}`
      : null;

  return (
    <>
      <TableCell sx={{ ...cellStyle, minWidth: 180 }}>
        {artistPath ? (
          <Link component={RouterLink} to={artistPath} sx={{ textDecoration: 'none' }}>
            <Typography {...textStyle}>{artist.name || '-'}</Typography>
          </Link>
        ) : (
          <Typography {...textStyle}>{artist.name || '-'}</Typography>
        )}
      </TableCell>
      <TableCell sx={{ ...cellStyle, minWidth: 300 }}>
        <Link component={RouterLink} to={getAlbumUrl(album)} sx={{ textDecoration: 'none' }}>
          <Typography {...textStyle}>{album.title}</Typography>
        </Link>
      </TableCell>
      <TableCell sx={{ ...cellStyle, minWidth: 180 }}>
        <Typography {...textStyle}>{album.genres?.join(', ') || '-'}</Typography>
      </TableCell>
      <TableCell sx={cellStyle}>
        <Typography {...textStyle}>{album.releaseDate || '-'}</Typography>
      </TableCell>
      <TableCell align="right" sx={cellStyle}>
        <Typography {...textStyle}>{Number(album.averageRating || 0).toFixed(1)}</Typography>
      </TableCell>
      <TableCell align="right" sx={cellStyle}>
        <Typography {...textStyle}>{Number(album.reviewCount || 0).toLocaleString()}</Typography>
      </TableCell>
      <TableCell align="center" sx={cellStyle}>
        <Typography {...textStyle}>{album.isAdMarked ? 'Y' : 'N'}</Typography>
      </TableCell>
      <TableCell align="center">
        <Typography {...textStyle}>{album.displayRegion || '-'}</Typography>
      </TableCell>
    </>
  );
};

export function reorderHomeAlbums(items, activeId, overId) {
  if (overId == null || activeId === overId) return items;
  const from = items.findIndex((item) => item.id === activeId);
  const to = items.findIndex((item) => item.id === overId);
  return from < 0 || to < 0 ? items : arrayMove(items, from, to);
}

function HomeAdMarkDialog({ action, onConfirm }) {
  const [marked, setMarked] = useState(action.confirmation.defaultAdMarked);
  return (
    <AdminBulkDialog action={action} title="광고 마크 관리" onConfirm={() => onConfirm(marked)}>
      <RadioGroup
        aria-label="광고 마크"
        sx={{ '& .MuiFormControlLabel-label': { wordBreak: 'keep-all' } }}
        value={String(marked)}
        onChange={(e) => setMarked(e.target.value === 'true')}
      >
        <FormControlLabel
          value="true"
          control={<Radio disabled={action.busy} />}
          label="광고 마크 추가"
        />
        <FormControlLabel
          value="false"
          control={<Radio disabled={action.busy} />}
          label="광고 마크 해제"
        />
      </RadioGroup>
    </AdminBulkDialog>
  );
}

const AlbumSection = ({
  title,
  section,
  albums,
  onOrderUpdate,
  onAdMarkUpdate,
  cellStyle,
  textStyle,
  buttonStyle,
}) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [orderedAlbums, setOrderedAlbums] = useState([]);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const alive = useRef(true);
  const markAction = useAdminBulkAction(() => setSelectedIds([]));
  const guard = useAdminDraftGuard(
    orderedAlbums.map((album) => album.id),
    { enabled: isEditingOrder, busy: saving },
  );
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  useEffect(() => {
    setSelectedIds((ids) => ids.filter((id) => albums.some((album) => album.id === id)));
  }, [albums]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const displayAlbums = isEditingOrder ? orderedAlbums : albums;
  const numSelected = albums.filter((album) => selectedIds.includes(album.id)).length;
  const isAllSelected = albums.length > 0 && numSelected === albums.length;
  const isIndeterminate = numSelected > 0 && !isAllSelected;
  const hasSelection = selectedIds.length > 0;
  const headCells = HEAD_CELLS;

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      setSelectedIds(albums.map((album) => album.id));
      return;
    }
    setSelectedIds([]);
  };

  const handleSelectClick = (event, id) => {
    event.stopPropagation();
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((albumId) => albumId !== id) : [...prev, id],
    );
  };

  const handleStartEditOrder = () => {
    setSaveError('');
    setOrderedAlbums([...albums]);
    setIsEditingOrder(true);
  };

  const handleCancelEditOrder = () => {
    if (pending.current) return;
    guard.markClean();
    setSaveError('');
    setOrderedAlbums([]);
    setIsEditingOrder(false);
  };

  const handleSaveOrder = async () => {
    if (pending.current) return;
    pending.current = true;
    setSaving(true);
    setSaveError('');
    const ids = orderedAlbums.map((album) => album.id);
    try {
      await onOrderUpdate(section, ids);
      if (!alive.current) return;
      guard.markClean();
      setSelectedIds([]);
      setIsEditingOrder(false);
    } catch (_error) {
      if (alive.current)
        setSaveError('순서를 저장하지 못했습니다. 편집한 순서를 유지했으니 다시 시도해주세요.');
    } finally {
      pending.current = false;
      if (alive.current) setSaving(false);
    }
  };

  const handleAdMarkUpdate = (marked) =>
    markAction.run((ids) =>
      onAdMarkUpdate(section, ids, markAction.confirmation.visibleIds, marked),
    );
  const handleDragEnd = ({ active, over }) => {
    if (!pending.current)
      setOrderedAlbums((items) => reorderHomeAlbums(items, active?.id, over?.id));
  };

  return (
    <Box component="section" aria-label={title}>
      <SectionTitle title={title} />

      <Stack
        direction="row"
        useFlexGap
        spacing={1}
        sx={{ flexWrap: 'wrap', mb: { xs: '20px', md: '16px' } }}
      >
        {isEditingOrder ? (
          <>
            <BasicButton
              variant="outlined"
              onClick={handleSaveOrder}
              disabled={saving}
              sx={buttonStyle}
            >
              {saving ? '저장 중…' : '저장'}
            </BasicButton>
            <BasicButton
              variant="outlined"
              onClick={handleCancelEditOrder}
              disabled={saving}
              sx={buttonStyle}
            >
              취소
            </BasicButton>
          </>
        ) : (
          <>
            <BasicButton
              variant="outlined"
              onClick={handleStartEditOrder}
              disabled={albums.length < 2}
              sx={buttonStyle}
            >
              순서 변경
            </BasicButton>
            <BasicButton
              variant="outlined"
              onClick={() =>
                markAction.open(selectedIds, {
                  visibleIds: albums.map((album) => album.id),
                  defaultAdMarked: !albums
                    .filter((album) => selectedIds.includes(album.id))
                    .every((album) => !album.isAdMarked),
                })
              }
              disabled={!hasSelection}
              sx={buttonStyle}
            >
              광고 마크 관리
            </BasicButton>
          </>
        )}
      </Stack>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {saveError}
        </Alert>
      )}
      <Typography role="status" sx={{ mb: 2 }}>
        선택한 앨범 {selectedIds.length}개
      </Typography>
      <TableContainer
        component={Paper}
        sx={{ boxShadow: 'none', maxWidth: '100%', overflowX: 'auto' }}
      >
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.grey.bg }}>
                <TableCell padding="checkbox" sx={cellStyle}>
                  {isEditingOrder ? (
                    <Box sx={{ width: 42, height: 42 }} />
                  ) : (
                    <Checkbox
                      inputProps={{ 'aria-label': `${title} 전체 선택` }}
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={handleSelectAllClick}
                      size="small"
                      disabled={albums.length === 0}
                      sx={{
                        color: theme.palette.grey.line,
                        '&.Mui-checked': { color: '#f16438' },
                        '&.MuiCheckbox-indeterminate': { color: '#f16438' },
                      }}
                    />
                  )}
                </TableCell>
                {headCells.map((headCell, index) => (
                  <TableCell
                    key={headCell.id}
                    align="center"
                    sx={{
                      whiteSpace: 'nowrap',
                      borderRight:
                        index === headCells.length - 1
                          ? 'none'
                          : `1px solid ${theme.palette.grey.line}`,
                    }}
                  >
                    <Typography fontSize={14} color={theme.palette.black[4]}>
                      {headCell.label}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isEditingOrder ? (
                <SortableContext
                  items={orderedAlbums.map((album) => album.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedAlbums.map((album) => (
                    <SortableAlbumRow
                      key={album.id}
                      album={album}
                      disabled={saving}
                      cellStyle={cellStyle}
                      textStyle={textStyle}
                    />
                  ))}
                </SortableContext>
              ) : (
                displayAlbums.map((album) => (
                  <TableRow key={album.id} hover>
                    <TableCell padding="checkbox" sx={cellStyle}>
                      <Checkbox
                        inputProps={{ 'aria-label': `${album.title} 선택` }}
                        checked={selectedIds.includes(album.id)}
                        onClick={(event) => handleSelectClick(event, album.id)}
                        size="small"
                        sx={{
                          color: theme.palette.grey.line,
                          '&.Mui-checked': { color: '#f16438' },
                        }}
                      />
                    </TableCell>
                    <AlbumCells album={album} cellStyle={cellStyle} textStyle={textStyle} />
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DndContext>
      </TableContainer>
      {albums.length === 0 && <NoData message="등록된 앨범이 없습니다" />}
      {markAction.confirmation && (
        <HomeAdMarkDialog action={markAction} onConfirm={handleAdMarkUpdate} />
      )}
    </Box>
  );
};

const HomeReviewSession = ({ onCountsChange }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [koreanAlbums, setKoreanAlbums] = useState([]);
  const [globalAlbums, setGlobalAlbums] = useState([]);
  const [recommendedAlbums, setRecommendedAlbums] = useState([]);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const loaded = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  useEffect(() => {
    let current = true;
    const fetchData = async () => {
      if (!loaded.current) setLoading(true);
      setError(null);
      try {
        const response = await adminApi.getHomeAlbums();
        if (!current) return;
        loaded.current = true;
        setKoreanAlbums(response.data.koreanNewReleaseAlbums || []);
        setGlobalAlbums(response.data.globalNewReleaseAlbums || []);
        setRecommendedAlbums(response.data.recommendedAlbums || []);
      } catch (err) {
        if (!current) return;
        setError(err);
      } finally {
        if (current) setLoading(false);
      }
    };

    fetchData();
    return () => {
      current = false;
    };
  }, [refetchTrigger]);

  const handleOrderUpdate = async (section, albumIds) => {
    await adminApi.updateHomeAlbumOrder(section, albumIds);
    if (!alive.current) return;
    setRefetchTrigger((c) => c + 1);
    onCountsChange?.();
  };

  const handleAdMarkUpdate = async (section, albumIds, visibleAlbumIds, isAdMarked) => {
    await adminApi.updateHomeAlbumAdMark(section, albumIds, visibleAlbumIds, isAdMarked);
    if (!alive.current) return;
    setRefetchTrigger((c) => c + 1);
    onCountsChange?.();
  };

  const cellStyle = useMemo(() => ({ borderRight: `1px solid ${theme.palette.grey.line}` }), []);
  const textStyle = useMemo(() => ({ fontSize: 14, color: theme.palette.black[9] }), []);
  const buttonStyle = useMemo(
    () => ({ typography: 'body3', fontWeight: 'medium', padding: '12px 16px' }),
    [],
  );

  return (
    <ProgressBar isLoading={loading}>
      {error && !loaded.current ? (
        <Alert
          severity="error"
          action={
            <BasicButton onClick={() => setRefetchTrigger((c) => c + 1)}>다시 시도</BasicButton>
          }
        >
          목록을 불러오지 못했습니다.
        </Alert>
      ) : (
        <Stack spacing={{ xs: '30px', md: '50px', lg: '60px' }}>
          {error && (
            <Alert
              severity="warning"
              action={
                <BasicButton onClick={() => setRefetchTrigger((c) => c + 1)}>다시 조회</BasicButton>
              }
            >
              저장은 완료됐지만 최신 목록을 불러오지 못했습니다. 다시 조회해주세요.
            </Alert>
          )}
          <AlbumSection
            title="최신 앨범 | 국내"
            section="korean"
            albums={koreanAlbums}
            onOrderUpdate={handleOrderUpdate}
            onAdMarkUpdate={handleAdMarkUpdate}
            cellStyle={cellStyle}
            textStyle={textStyle}
            buttonStyle={buttonStyle}
          />
          <AlbumSection
            title="최신 앨범 | 해외"
            section="global"
            albums={globalAlbums}
            onOrderUpdate={handleOrderUpdate}
            onAdMarkUpdate={handleAdMarkUpdate}
            cellStyle={cellStyle}
            textStyle={textStyle}
            buttonStyle={buttonStyle}
          />
          <AlbumSection
            title="포스트팝 추천"
            section="recommended"
            albums={recommendedAlbums}
            onOrderUpdate={handleOrderUpdate}
            onAdMarkUpdate={handleAdMarkUpdate}
            cellStyle={cellStyle}
            textStyle={textStyle}
            buttonStyle={buttonStyle}
          />
        </Stack>
      )}
    </ProgressBar>
  );
};

export default function HomeReviewManagement(props) {
  const user = useUserStore((state) => state.user);
  return (
    <HomeReviewSession key={JSON.stringify([user?.id ?? null, user?.role ?? null])} {...props} />
  );
}
